import React, { useState, useEffect } from "react";
import TableContainer from "@material-ui/core/TableContainer";
import {
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  withStyles,
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import { config } from "../../config/config";
import Loader from "../Loader/Loader";
import DynamicDialog from "../Dialog/Dialog";
import Button from "@mui/material/Button";
import Tablestyles from "../../screens/Mapping/ROCITable.module.css";
import HistoryAndLogsModal from "./HistoryAndLogsModal";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

let api = config.api.api_url;

function MetaSearchFilter(props) {
  let { user, sourceData, login, updateSourceInputs, updateAlert, loading } =
    props;

  const [isCountryLoading, setIsCountryLoading] = useState(false);
  const [isFileIdLoading, setIsFileIdLoading] = useState(false);

  // FETCH META DATA FOR SELECTED DATASET
  const handleSourceChange = async (e, selectedOption) => {
    updateSourceInputs("current_source_logs", selectedOption.value);
    updateSourceInputs("current_target_gold_layer_logs", "");
    updateSourceInputs("is_initial_logs", false);
    updateSourceInputs("current_country_logs", "");
    updateSourceInputs("current_country_code_logs", "");
    updateSourceInputs("current_ssfFileId_logs", "");

    const Target_gold_layer = sourceData.data_logs.data.find((item) => {
      return item.dataset_name === selectedOption.value;
    });
    updateSourceInputs(
      "current_target_gold_layer_list_logs",
      Target_gold_layer.TARGET_GOLD_LAYER
    );
  };

  const handleTargetGoldLayerChange = async (e, selectedOption) => {
    setIsCountryLoading(true);
    updateSourceInputs("current_target_gold_layer_logs", selectedOption);
    updateSourceInputs("is_initial_logs", false);
    updateSourceInputs("current_country_logs", "");
    updateSourceInputs("current_country_code_logs", "");
    updateSourceInputs("current_ssfFileId_logs", "");

    const params = new URLSearchParams({
      filter_type: "history",
      dataset_name: selectedOption?.value || "",
      gold_layer: selectedOption?.label || "",
    });
    try {
      const response = await fetch(
        api + `/gold_template_mapping/getcountries?${params}`,
        {
          method: "GET",
          mode: "cors",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${user.token || ""}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      // Check if response is OK before trying to parse it
      if (!response.ok) {
        if (data?.statusCode === "harbor-401") {
          updateAlert({
            open: true,
            message: `User Unauthorized. Please sign in.`,
            severity: "warning",
            closeFlag: true,
          });
          login(); // or redirect to login
          return;
        } else {
          throw new Error(`Unexpected error occured. Please try again later`);
        }
      }

      if (!data || !data.data) {
        throw new Error("Unexpected error occured. Please try again later");
      }

      const NEW_COUNTRIES = data.data;

      const countryOptions = NEW_COUNTRIES.filter((item) => item != null) // filters out null and undefined
        .map((country) => ({
          label: country,
          value: country,
        }));

      updateSourceInputs("current_mapped_country_logs", countryOptions);
    } catch (err) {
      updateAlert({
        open: true,
        message: `Unexpected error occured. Please try again later`,
        severity: "Failed ",
        closeFlag: true,
      });
    }
    setIsCountryLoading(false);
  };

  // FETCH SFF_FILE_ID'S FOR SELECTED COUNTRY
  const handleCountryChange = async (e, selectedOption) => {
    setIsFileIdLoading(true);
    updateSourceInputs("is_initial_logs", false);
    updateSourceInputs("current_ssfFileId_logs", "");
    updateSourceInputs("current_ssf_file_id_logs", []);

    updateSourceInputs("current_country_logs", selectedOption.label);
    updateSourceInputs("current_country_code_logs", selectedOption.value);

    // API call to get Updated SFF_File_Id based on Filter
    const params = new URLSearchParams({
      country: selectedOption.label || "",
      type: "history",
      dataset_name: sourceData?.current_target_gold_layer_logs?.value || "",
      target_gold_layer:
        sourceData?.current_target_gold_layer_logs?.label || "",
    });
    try {
      const response = await fetch(
        api + `/gold_template_mapping/get_harbor_source_file_id?${params}`,
        {
          method: "GET",
          mode: "cors",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${user.token || ""}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      // Check if response is OK before trying to parse it
      if (!response.ok) {
        if (data?.statusCode === "harbor-401") {
          updateAlert({
            open: true,
            message: `User Unauthorized. Please sign in.`,
            severity: "warning",
            closeFlag: true,
          });
          login(); // or redirect to login
          return;
        } else {
          throw new Error(`Unexpected error occured. Please try again later`);
        }
      }

      if (!data || !data.data) {
        throw new Error("Unexpected error occured. Please try again later");
      }

      const NEW_SFF_FILE_IDS = data.data;

      const UPDATED_NEW_SFF_FILE_IDS =
        Array.isArray(NEW_SFF_FILE_IDS) &&
        NEW_SFF_FILE_IDS.length === 1 &&
        NEW_SFF_FILE_IDS[0] === ""
          ? []
          : NEW_SFF_FILE_IDS.map((item) => {
              return { label: item, value: item };
            });

      updateSourceInputs("current_ssf_file_id_logs", UPDATED_NEW_SFF_FILE_IDS);
    } catch (err) {
      updateAlert({
        open: true,
        message: `Unexpected error occured. Please try again later`,
        severity: "Failed ",
        closeFlag: true,
      });
    }
    setIsFileIdLoading(false);
  };

  // FETCH TABLE DATA FOR SELECTED SFF_FILE_ID
  const handleSSFFileChange = async (e, selectedOption) => {
    updateSourceInputs("is_initial_logs", true);
    updateSourceInputs("is_table_loading_logs", true);
    updateSourceInputs("current_ssfFileId_logs", selectedOption.value);

    try {
      const params = new URLSearchParams({
        usecase: "gold_template_mapping",
        dataset_name: sourceData?.current_target_gold_layer_logs?.value || "",
        country_code: sourceData?.current_country_logs || "",
        fileid: selectedOption?.value || "",
      });
      const response = await fetch(
        api + `/gold_template_mapping/get_approval_table_logs?${params}`,
        {
          method: "GET",
          mode: "cors",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${user.token || ""}`,
            "Content-Type": "application/json",
          },
        }
      );
      // Check if response is OK before trying to parse it
      if (!response.ok) {
        throw new Error(`Unexpected error occured. Please try again later`);
      }

      const data = await response.json();

      // Check if response is OK before trying to parse it
      if (!response.ok) {
        if (data?.statusCode === "harbor-401") {
          updateAlert({
            open: true,
            message: `User Unauthorized. Please sign in.`,
            severity: "warning",
            closeFlag: true,
          });
          login(); // or redirect to login
          return;
        } else {
          throw new Error(`Unexpected error occured. Please try again later`);
        }
      }

      if (!data || !data.data) {
        throw new Error("Unexpected error occured. Please try again later");
      }

      const HISTORY_TABLE_HEADER = Object.keys(data?.data?.[0] || {});
      const FINAL_HISTORY_TABLE_HEADER = HISTORY_TABLE_HEADER.map((item) => ({
        label: item,
        name: item,
      }));

      updateSourceInputs("historyData", data.data);
      updateSourceInputs(
        "historyTable_Header",
        FINAL_HISTORY_TABLE_HEADER || []
      );
      updateSourceInputs("is_table_loading_logs", false);
    } catch (err) {
      updateAlert({
        open: true,
        message: `Unexpected error occured. Please try again later`,
        severity: "success",
        closeFlag: true,
      });
      updateSourceInputs("is_table_loading_logs", false);
    }
  };

  return (
    <>
      <div className="cnc-model-header-mapping">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)", // 5 equal-width columns
            gridTemplateRows: "1fr", // 1 row
            gap: "12px", // gap between columns (and rows if any)
            padding: "2px 15px 0px 15px",
            width: "100%",
          }}
        >
          <>
            {/* DATASETS */}
            <div item xs={2} sm={2}>
              <div className="mg-model-header-item" style={{ height: "25px" }}>
                Select Dataset:{" "}
                <p
                  style={{
                    display: "inline",
                    color: "red",
                    fontSize: 16,
                  }}
                >
                  *
                </p>
              </div>
              <div className="" style={{ marginBottom: "15px" }}>
                <Autocomplete
                  id="country-select"
                  disableClearable
                  size="small"
                  options={sourceData?.current_mapped_source_logs || []}
                  loading={
                    loading &&
                    (sourceData.current_mapped_source_logs ||
                      sourceData.current_mapped_source_logs.length === 0)
                  }
                  loadingText={<Loader />}
                  getOptionLabel={(option) => option.label}
                  onChange={handleSourceChange}
                  value={
                    sourceData.current_source_logs &&
                    sourceData.current_source_logs.length > 0
                      ? {
                          label: sourceData.current_source_logs,
                          value: sourceData.current_source_logs,
                        }
                      : null
                  }
                  popupIcon={
                    <span
                      className="material-icons"
                      style={{
                        fontSize: "20px",
                        color:
                          sourceData.current_source_logs === ""
                            ? "f7f7f7"
                            : "#555",
                      }}
                    >
                      arrow_drop_down
                    </span>
                  }
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fff",
                      fontFamily: "Hind Siliguri",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "#333",
                      height: "36px",
                      paddingRight: "36px", // space for dropdown icon
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 12px",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)", // vertical center alignment
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#999",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      // label="Select Country"
                      InputLabelProps={{
                        style: {
                          fontSize: "13px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        color: "#212529",
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
              </div>
            </div>

            {/* TARGET GOLD LAYER */}
            <div>
              <div className="mg-model-header-item" style={{ height: "25px" }}>
                Select Target Gold Layer:{" "}
                <p
                  style={{
                    display: "inline",
                    color: "red",
                    fontSize: 16,
                  }}
                >
                  *
                </p>
              </div>
              <div className="" style={{ marginBottom: "15px" }}>
                <Autocomplete
                  id="country-select"
                  disableClearable
                  size="small"
                  options={sourceData.current_target_gold_layer_list_logs}
                  loading={
                    !sourceData?.current_target_gold_layer_list_logs ||
                    sourceData?.current_target_gold_layer_list_logs.length === 0
                  }
                  disabled={
                    !sourceData.current_source_logs ||
                    sourceData.current_source_logs.length === 0
                  }
                  loadingText={<Loader />}
                  getOptionLabel={(option) => option.label}
                  onChange={handleTargetGoldLayerChange}
                  value={
                    sourceData.current_target_gold_layer_logs &&
                    Object.keys(sourceData.current_target_gold_layer_logs)
                      .length > 0
                      ? sourceData.current_target_gold_layer_logs
                      : null
                  }
                  popupIcon={
                    <span
                      className="material-icons"
                      style={{
                        fontSize: "20px",
                        color:
                          sourceData.current_source_logs === ""
                            ? "f7f7f7"
                            : "#555",
                      }}
                    >
                      arrow_drop_down
                    </span>
                  }
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor:
                        sourceData.current_source_logs === ""
                          ? "#d6d6d6"
                          : "#fff",
                      fontFamily: "Hind Siliguri",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "#333",
                      height: "36px",
                      paddingRight: "36px", // space for dropdown icon
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 12px",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)", // vertical center alignment
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#999",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      // label="Select Country"
                      InputLabelProps={{
                        style: {
                          fontSize: "13px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        color: "#212529",
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
              </div>
            </div>

            {/* COUNTRY */}
            <div>
              <div className="mg-model-header-item" style={{ height: "25px" }}>
                Select Country Code:{" "}
                <p
                  style={{
                    display: "inline",
                    color: "red",
                    fontSize: 16,
                  }}
                >
                  *
                </p>
              </div>
              <div className="" style={{ marginBottom: "15px" }}>
                <Autocomplete
                  disabled={
                    sourceData.current_target_gold_layer_logs === "" ||
                    loading == true
                      ? true
                      : false
                  }
                  loading={isCountryLoading}
                  loadingText={<Loader />}
                  id="country-select"
                  disableClearable
                  size="small"
                  options={sourceData.current_mapped_country_logs}
                  getOptionLabel={(option) => option.label}
                  onChange={handleCountryChange}
                  defaultValue={
                    {
                      label: sourceData.current_country_logs,
                      value: sourceData.current_country_logs,
                    } || null
                  }
                  popupIcon={
                    <span
                      className="material-icons"
                      style={{
                        fontSize: "20px",
                        color:
                          sourceData.current_target_gold_layer_logs === ""
                            ? "f7f7f7"
                            : "#555",
                      }}
                    >
                      arrow_drop_down
                    </span>
                  }
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor:
                        sourceData.current_target_gold_layer_logs === ""
                          ? "#d6d6d6"
                          : "#fff",
                      fontFamily: "Hind Siliguri",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "#333",
                      height: "36px",
                      paddingRight: "36px", // space for dropdown icon
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 12px",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)", // vertical center alignment
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#999",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      // label="Select Country"
                      InputLabelProps={{
                        style: {
                          fontSize: "13px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        color: "#212529",
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
              </div>
            </div>

            {/* SFF FILE ID  */}
            <div>
              <div className="mg-model-header-item" style={{ height: "25px" }}>
                Select SFF File ID:{" "}
                <p
                  style={{
                    display: "inline",
                    color: "red",
                    fontSize: 16,
                  }}
                >
                  *
                </p>
              </div>
              <div className="" style={{ marginBottom: "15px" }}>
                <Autocomplete
                  id="country-select"
                  disableClearable
                  size="small"
                  options={sourceData.current_ssf_file_id_logs}
                  loading={isFileIdLoading}
                  disabled={
                    !sourceData.current_country_logs ||
                    sourceData.current_country_logs.length === 0
                  }
                  loadingText={<Loader />}
                  getOptionLabel={(option) => option.label}
                  onChange={handleSSFFileChange}
                  value={
                    sourceData.current_ssfFileId_logs &&
                    sourceData.current_ssfFileId_logs.length > 0
                      ? {
                          label: sourceData.current_ssfFileId_logs,
                          value: sourceData.current_ssfFileId_logs,
                        }
                      : null
                  }
                  popupIcon={
                    <span
                      className="material-icons"
                      style={{
                        fontSize: "20px",
                        color:
                          sourceData.current_country_logs === ""
                            ? "f7f7f7"
                            : "#555",
                      }}
                    >
                      arrow_drop_down
                    </span>
                  }
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor:
                        sourceData.current_country_logs === ""
                          ? "#d6d6d6"
                          : "#fff",
                      fontFamily: "Hind Siliguri",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: "400",
                      color: "#333",
                      height: "36px",
                      paddingRight: "36px", // space for dropdown icon
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 12px",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)", // vertical center alignment
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#999",
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      // label="Select Country"
                      InputLabelProps={{
                        style: {
                          fontSize: "13px",
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        color: "#212529",
                      }}
                    >
                      {option.label}
                    </li>
                  )}
                />
              </div>
            </div>
          </>
        </div>
      </div>

      {/* {isPlaceholderFetching && <Loader />} */}
    </>
  );
}

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: "#e5e5e5",
    color: "#212529",
    height: 3,
    width: "auto", // let content decide
    minWidth: "50px",
    maxWidth: "250px",
    whiteSpace: "normal",
  },
  body: {
    fontSize: 12,
    fontFamily: "Roboto,Helvetica,Arial,sans-serif",
    width: "auto", // let content decide
    minWidth: "50px",
    maxWidth: "250px",
    whiteSpace: "normal",
  },
  root: {
    padding: "15px",
  },
}))(TableCell);

const Row = ({
  keys,
  rowData,
  headingRow,
  isLoading,
  handleModalOpen,
  handleDynamicModal,
  usecase_name,
}) => {
  let data = rowData;

  const handleOpenModal = () => {
    handleModalOpen(rowData);
  };

  return (
    <TableRow
      key={`Rowkey-${keys}`}
      className={"table-row"}
      style={{ cursor: "pointer" }}
    >
      {headingRow.map(({ label, name }, index) => (
        <>
          <StyledTableCell
            key={`key-${index}`}
            className={""}
            onClick={handleOpenModal}
            style={{
              position: "relative",
              height: "45px",
              padding: "2px 3px",
              whiteSpace: "nowrap",
              textWrap: "wrap",
              cursor: "pointer",
            }}
          >
            {data[name] && typeof data[name] === "object"
              ? JSON.stringify(data[name])
              : data[name] || "None"}
          </StyledTableCell>
        </>
      ))}
    </TableRow>
  );
};

export const HistoryAndLogsTable = ({
  sourceData,
  user,
  updateAlert,
  login,
  updateSourceInputs,
  usecase_name,
  loading,
}) => {
  // Table State's
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginatedRows, setPaginatedRows] = useState([]);
  const [isTableLoading, setTableLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const TableData = sourceData?.historyData || [];
  const TableHeader = [...sourceData?.historyTable_Header];

  // Modal State
  const [openModal, setOpenModal] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionType, setActionType] = useState("");
  const [currentDataset, setCurrentDataset] = useState("");
  const [currentFileId, setCurrentFileId] = useState("");
  const [currentCountry, setCurrentCountry] = useState("");
  const [currentSource, setCurrentSource] = useState("");
  const [historyAndLogsTableHeaders, setHistoryAndLogsTableHeaders] = useState(
    []
  );

  const getHistoryAndLogsTableData = async (rowData) => {
    try {
      const BODY = {
        dataset_name: sourceData?.current_target_gold_layer_logs?.value || "",
        country: rowData?.COUNTRY || "",
        file_id: rowData?.HARBOR_SOURCE_FILE_ID || "",
        target_gold_layer: rowData?.TARGET_GOLD_LAYER || "",
        timestamp: rowData?.TIMESTAMP || "",
        usecase: "gold_template_mapping",
      };
      const response = await fetch(
        api + `/gold_template_mapping/get_logs_table_data`,
        {
          method: "POST",
          mode: "cors",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${user.token || ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(BODY),
        }
      );
      // Check if response is OK before trying to parse it
      if (!response.ok) {
        throw new Error(`Unexpected error occured. Please try again later`);
      }

      const data = await response.json();

      // Check if response is OK before trying to parse it
      if (!response.ok) {
        if (data?.statusCode === "harbor-401") {
          updateAlert({
            open: true,
            message: `User Unauthorized. Please sign in.`,
            severity: "warning",
            closeFlag: true,
          });
          login(); // or redirect to login
          return;
        } else {
          throw new Error(`Unexpected error occured. Please try again later`);
        }
      }

      if (!data || !data.data) {
        throw new Error("Unexpected error occured. Please try again later");
      }

      const TableHeaderColumnList = Object.keys(data?.data?.[0] || {});
      const FINAL_TABLE_HEADER_COLUMNS =
        TableHeaderColumnList?.map((item) => ({ label: item, name: item })) ??
        [];

      setTableData(data.data || []);
      setHistoryAndLogsTableHeaders(FINAL_TABLE_HEADER_COLUMNS);
    } catch (err) {
      updateAlert({
        open: true,
        message: `Unexpected error occured. Please try again later`,
        severity: "success",
        closeFlag: true,
      });
    } finally {
      setTableLoading(false);
    }
  };

  const handleModalOpen = (rowData) => {
    setOpenModal(!openModal);
    setTableLoading(true);
    getHistoryAndLogsTableData(rowData);
  };

  const handleDynamicModal = (
    dataset,
    file_id,
    country,
    actionType,
    metadata,
    rowdata,
    source
  ) => {
    setActionType(actionType);
    setCurrentDataset(dataset);
    setCurrentFileId(file_id);
    setCurrentCountry(country);
    setOpenDialog(!openDialog);
    setCurrentSource(source);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setOpenDialog(false);
  };

  const handlePaginatedRows = (newPage) => {
    const start = newPage * rowsPerPage;
    const end = start + rowsPerPage;
    const currentPageRows = TableData.slice(start, end);
    return currentPageRows;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const Paginated_Rows = handlePaginatedRows(newPage);
    setPaginatedRows(Paginated_Rows);
  };

  const handleChangeRowsPerPage = (e) => {
    const value = parseInt(e.target.value);
    setRowsPerPage(value);
    const start = 0 * value;
    const end = start + value;
    const currentPageRows = TableData.slice(start, end);
    setPaginatedRows(currentPageRows);
    setPage(0);
  };

  useEffect(() => {
    setPaginatedRows(TableData.slice(0, rowsPerPage));
  }, [TableData]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MetaSearchFilter
          user={user}
          sourceData={sourceData}
          login={login}
          updateSourceInputs={updateSourceInputs}
          updateAlert={updateAlert}
          loading={loading}
        />
      )}

      {sourceData.is_initial_logs &&
        (sourceData.is_table_loading_logs ? (
          <Loader />
        ) : (
          <div style={{ marginTop: "20px" }}>
            <TableContainer className={Tablestyles.budget_tb3}>
              <Table stickyHeader className={""}>
                <TableHead className="table-heading">
                  <TableRow className={""}>
                    {TableHeader.map(({ label, name }, i) => (
                      <StyledTableCell key={i}>{label}</StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row, index) => (
                    <Row
                      keys={index}
                      rowData={row}
                      headingRow={TableHeader}
                      isLoading={isLoading}
                      handleModalOpen={handleModalOpen}
                      handleDynamicModal={handleDynamicModal}
                      usecase_name={usecase_name}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {!TableData.length && (
              <div style={{ textAlign: "center", marginTop: "10px" }}>
                No History and Logs found!
              </div>
            )}

            {/* Pagination */}
            {TableData.length > 0 && (
              <div>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={TableData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            )}
          </div>
        ))}

      {/* Modal to show single File_Id's Table Data */}
      <HistoryAndLogsModal
        sourceData={sourceData}
        openModal={openModal}
        closeModel={handleCloseModal}
        isTableLoading={isTableLoading}
        tableData={tableData}
        updateAlert={updateAlert}
        historyAndLogsTableHeaders={historyAndLogsTableHeaders}
      />

      {/* Genral Dialog */}
      <DynamicDialog
        openModal={openDialog}
        closeModel={handleCloseModal}
        hasCustomCancelButton={false}
        title={"Confirmation"}
        content={
          <p
            style={{ fontSize: "16px", color: "black", marginTop: "6px" }}
          >{`Records of this file_Id will be ${
            actionType && actionType === "Approve" ? "Approved" : "rejected"
          }. Are you sure?`}</p>
        }
        actions={
          <>
            {actionType && actionType === "Approve" && (
              <Button
                onClick={() => {}}
                style={{
                  backgroundColor: "#46596A",
                  fontFamily: "Hind Siliguri",
                  fontSize: "12px",
                  fontWeight: "bold",
                  marginRight: "5px",
                  color: "white",
                }}
              >
                Continue {isLoading && <Loader />}
              </Button>
            )}
            {actionType && actionType === "Reject" && (
              <Button
                onClick={() => {}}
                style={{
                  backgroundColor: "#46596A",
                  fontFamily: "Hind Siliguri",
                  fontSize: "12px",
                  fontWeight: "bold",
                  marginRight: "5px",
                  color: "white",
                }}
              >
                Continue {isLoading && <Loader />}
              </Button>
            )}
          </>
        }
      />
    </>
  );
};
